import React from 'react';
import { ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const MABiblicalLanguages = () => {
  const coreCourses = [
    { code: 'BIB 502', name: 'Introduction to Biblical Languages', credits: 3 },
    { code: 'BIB 531', name: 'Elementary Greek 1', credits: 3 },
    { code: 'BIB 532', name: 'Elementary Greek 2', credits: 3 },
    { code: 'BIB 533', name: 'Advanced Greek 1', credits: 3 },
    { code: 'BIB 534', name: 'Advanced Greek 2', credits: 3 },
    { code: 'BIB 541', name: 'Elementary Hebrew 1', credits: 3 },
    { code: 'BIB 542', name: 'Elementary Hebrew 2', credits: 3 },
    { code: 'BIB 543', name: 'Advanced Hebrew', credits: 3 },
    { code: 'BIB 621', name: 'Old Testament Textual Criticism', credits: 3 },
    { code: 'BIB 622', name: 'New Testament Textual Criticism', credits: 3 }
  ];

  const thesisCourses = [
    { code: 'BIB 591', name: 'Research / Directed Readings', credits: 3 },
    { code: 'BIB 592', name: 'Thesis', credits: 3 }
  ];

  const programFeatures = [
    {
      title: "Biblical Languages",
      description: "Master both Hebrew and Greek for comprehensive biblical study",
      icon: "🔤"
    },
    {
      title: "Textual Criticism",
      description: "Advanced study of biblical manuscript traditions",
      icon: "📜"
    },
    {
      title: "Translation Skills",
      description: "Develop expertise in biblical translation",
      icon: "📝"
    },
    {
      title: "Research Methods",
      description: "Learn advanced biblical research methodology",
      icon: "🔍"
    }
  ];

  const resources = [
    {
      title: "Language Labs",
      description: "Access to Hebrew and Greek language resources",
      path: "/academics/resources/language_labs"
    },
    {
      title: "Manuscript Studies",
      description: "Digital access to ancient biblical manuscripts",
      path: "/academics/resources/manuscripts"
    },
    {
      title: "Translation Tools",
      description: "Professional translation and research tools",
      path: "/academics/resources/translation"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Master of Arts in Biblical Languages
            </h1>
            <p className="text-xl opacity-90">
              Advanced study in Hebrew and Greek for biblical research and translation
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                The MA in Biblical Languages provides comprehensive training in both Hebrew and Greek, 
                preparing students for advanced biblical research, translation work, and textual studies. 
                This program emphasizes hands-on engagement with biblical texts in their original languages.
              </p>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {programFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Core Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Core Courses (30 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {coreCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Thesis Requirements */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Thesis Requirement (6 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {thesisCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Resources Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Resources</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {resources.map((resource, index) => (
                  <ResourceLink
                    key={index}
                    to={resource.path}
                    title={resource.title}
                    description={resource.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Academic Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join our community of scholars dedicated to biblical language studies and research.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MABiblicalLanguages; 